import React from 'react';
import { AreaChart, Area, ResponsiveContainer, YAxis, Tooltip, XAxis } from 'recharts';

interface DataCardProps {
  title: string;
  value: string;
  isRealtime?: boolean;
  change?: number;
  changePeriod?: string;
  trendData?: { time: number; value: number }[];
}

const DataCard: React.FC<DataCardProps> = ({ title, value, isRealtime, change, changePeriod, trendData }) => {
  const isPositive = change !== undefined && change >= 0;
  const trendColor = isPositive ? '#22C55E' : '#EF4444'; // Matching Tailwind green-500 & red-500
  const trendGradientId = `trendGradient-${title.replace(/[\s/]/g, '-')}`;

  const renderChangeInfo = () => {
    if (change !== undefined) {
      return (
        <div className="flex items-center">
          <span className={`text-lg font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? '▲' : '▼'} {isPositive ? '+' : ''}{change.toFixed(2)}%
          </span>
          <span className="text-light-blue ml-2 text-sm">{changePeriod}</span>
        </div>
      );
    }
    if (isRealtime) {
      return (
        <div className="flex items-center space-x-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-green-400 text-lg font-semibold">Live</span>
        </div>
      );
    }
    return <div />; // Placeholder to maintain alignment
  };

  return (
    <div className="bg-navy-blue p-6 rounded-lg shadow-xl flex flex-col justify-between h-full">
      {/* Top Section: Title and Value */}
      <div>
        <h3 className="text-light-blue text-lg">{title}</h3>
        {value === 'Loading...' ? (
          <div className="bg-steel-blue/50 h-10 w-3/4 rounded-md animate-pulse mt-2"></div>
        ) : (
          <p className="text-4xl font-bold text-off-white mt-2">{value}</p>
        )}
      </div>

      {/* Bottom Section: Change and Chart */}
      <div className="flex items-end justify-between mt-4 h-16">
        {renderChangeInfo()}
        
        {trendData && trendData.length > 0 && change !== undefined && (
          <div className="w-1/2 h-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id={trendGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={trendColor} stopOpacity={0.5}/>
                    <stop offset="95%" stopColor={trendColor} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" hide={true} />
                <YAxis domain={['dataMin', 'dataMax']} hide={true} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(13, 27, 42, 0.9)',
                    border: `1px solid ${trendColor}`,
                    borderRadius: '0.5rem',
                    color: '#E0E1DD',
                    backdropFilter: 'blur(4px)',
                  }}
                  itemStyle={{ color: trendColor, fontWeight: 'bold' }}
                  formatter={(val: number) => `$${(val / 1e9).toFixed(2)}B`}
                  labelFormatter={(label: number) => {
                    const date = new Date(label);
                    const now = new Date();
                    // If the data point is within the last 5 minutes, label it as 'Now'
                    if (now.getTime() - date.getTime() < 5 * 60 * 1000) {
                        return 'Now';
                    }
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                  cursor={{ stroke: 'rgba(124, 58, 237, 0.5)', strokeWidth: 1 }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value"
                  name="Market Cap"
                  stroke={trendColor} 
                  strokeWidth={2.5}
                  fill={`url(#${trendGradientId})`}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataCard;